import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, CircularProgress, Alert, Chip, Stack,
  Autocomplete, useMediaQuery, useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { exportService } from '../../services/accounting/export.service';
import { serverMessage } from '../../utils/apiConvert';
import { toYmd } from '../../utils/accounting/periodUtils';

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }).format(amount || 0);

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

const getStatusColor = (status) => ({
  PAID: 'success', PARTIAL: 'warning', PENDING: 'info', VOIDED: 'error'
}[status] || 'default');

const getMethodLabel = (method) => ({
  WALLET: 'Wallet', CASH: 'Cash', BANK_TRANSFER: 'Bank Transfer', CHEQUE: 'Cheque', ONLINE: 'Online'
}[method] || method);

const InvoiceReceiptSummary = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [receiptTotal, setReceiptTotal] = useState(0);

  // Filters (default: the current month)
  const [dateFrom, setDateFrom] = useState(toYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [dateTo, setDateTo] = useState(toYmd(new Date()));
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    financeReportsService.getCustomers(user.typingCenterId || user.typing_center_id).then(setCustomers).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeReportsService.getInvoiceReceipts({ from: dateFrom, to: dateTo, customerId: selectedCustomer?.id });
      setData((res?.rows || []).map((r, i) => ({ ...r, id: `${r.receiptNumber}-${i}`, invoiceAmount: Number(r.invoiceAmount), receiptAmount: Number(r.receiptAmount) })));
      setReceiptTotal(Number(res?.receiptTotal || 0));
    } catch (err) {
      setData([]);
      setReceiptTotal(0);
      setError(serverMessage(err, 'Failed to load invoice receipt summary'));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedCustomer]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Export with the page's own filters (BE-14b); the file comes back under the server's name
  const [exporting, setExporting] = useState(null);
  const doExport = async (format) => {
    setExporting(format);
    try {
      await exportService.download('invoice-receipts', format, { from: dateFrom, to: dateTo, customerId: selectedCustomer?.id });
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(null);
    }
  };

  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Group by invoice for subtotals
  const invoiceGroups = {};
  data.forEach(row => {
    if (!invoiceGroups[row.invoiceNumber]) {
      invoiceGroups[row.invoiceNumber] = { invoiceAmount: row.invoiceAmount, receipts: [] };
    }
    invoiceGroups[row.invoiceNumber].receipts.push(row);
  });

  const totalInvoiced = Object.values(invoiceGroups).reduce((sum, g) => sum + g.invoiceAmount, 0);
  const totalReceived = receiptTotal;

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography sx={{ fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: 600, color: 'secondary.main' }}>
            Invoice Receipt Summary
          </Typography>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
            Invoices linked to their payment receipts
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button size="small" variant="contained" startIcon={<ExcelIcon sx={{ fontSize: 16 }} />}
            onClick={() => doExport('csv')} disabled={Boolean(exporting)}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, fontSize: fontSize.xs, textTransform: 'none' }}>
            {exporting === 'csv' ? 'Exporting…' : 'CSV'}
          </Button>
          <Button size="small" variant="contained" startIcon={<PdfIcon sx={{ fontSize: 16 }} />}
            onClick={() => doExport('pdf')} disabled={Boolean(exporting)}
            sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' }, fontSize: fontSize.xs, textTransform: 'none' }}>
            {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </Button>
          <Button size="small" variant="outlined" onClick={fetchData}
            sx={{ minWidth: 36 }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="From Date" type="date"
              value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="To Date" type="date"
              value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete size="small" options={customers}
              value={selectedCustomer}
              onChange={(e, v) => { setSelectedCustomer(v); setPage(0); }}
              renderInput={(params) => <TextField {...params} label="Customer / Organization" />}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table / Cards */}
      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : paginatedData.length === 0 ? (
            <Paper sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No records found</Typography></Paper>
          ) : paginatedData.map((row) => (
            <Card key={row.id} variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Box>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{row.invoiceNumber}</Typography>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{row.customer}</Typography>
                  </Box>
                  <Chip label={row.status} size="small" color={getStatusColor(row.status)} sx={{ fontSize: fontSize.xs, height: 22 }} />
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Receipt: {row.receiptNumber}</Typography>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{formatDate(row.date)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Invoice Amt</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{formatCurrency(row.invoiceAmount)}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Receipt Amt</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: '#4caf50' }}>{formatCurrency(row.receiptAmount)}</Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mt: 0.5 }}>Method: {getMethodLabel(row.paymentMethod)}</Typography>
              </CardContent>
            </Card>
          ))}
          {/* Mobile Summary */}
          {!loading && data.length > 0 && (
            <Card sx={{ bgcolor: isDark ? 'rgba(76,175,80,0.08)' : '#e8f5e9' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, mb: 1 }}>Totals</Typography>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Total Invoiced</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700 }}>{formatCurrency(totalInvoiced)}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Total Received</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: '#4caf50' }}>{formatCurrency(totalReceived)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Invoice #</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Customer</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Receipt #</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Payment Method</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Invoice Amount</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Receipt Amount</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No records found</Typography></TableCell></TableRow>
                ) : (
                  <>
                    {paginatedData.map((row, idx) => {
                      // Check if this is first row of an invoice group with multiple receipts
                      const sameInvoiceRows = paginatedData.filter(r => r.invoiceNumber === row.invoiceNumber);
                      const isLastOfGroup = sameInvoiceRows.length > 1 && sameInvoiceRows[sameInvoiceRows.length - 1].id === row.id;
                      const groupReceiptTotal = sameInvoiceRows.reduce((s, r) => s + r.receiptAmount, 0);

                      return (
                        <React.Fragment key={row.id}>
                          <TableRow hover>
                            <TableCell sx={{ fontSize: '13px', py: 1, color: 'text.secondary' }}>{formatDate(row.date)}</TableCell>
                            <TableCell sx={{ fontSize: '13px', py: 1 }}>
                              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'secondary.main' }}>{row.invoiceNumber}</Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: '13px', py: 1 }}>{row.customer}</TableCell>
                            <TableCell sx={{ fontSize: '13px', py: 1 }}>
                              <Chip label={row.receiptNumber} size="small" variant="outlined" sx={{ fontSize: fontSize.xs, height: 22 }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: '13px', py: 1 }}>{getMethodLabel(row.paymentMethod)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '13px', py: 1, fontWeight: 500 }}>{formatCurrency(row.invoiceAmount)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '13px', py: 1, fontWeight: 600, color: row.status === 'VOIDED' ? '#f44336' : '#4caf50' }}>
                              {row.status === 'VOIDED' ? `(${formatCurrency(row.receiptAmount)})` : formatCurrency(row.receiptAmount)}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1 }}>
                              <Chip label={row.status} size="small" color={getStatusColor(row.status)} sx={{ fontSize: fontSize.xs, height: 22 }} />
                            </TableCell>
                          </TableRow>
                          {/* Subtotal row for multi-receipt invoices */}
                          {isLastOfGroup && (
                            <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                              <TableCell colSpan={5} />
                              <TableCell align="right" sx={{ fontSize: '13px', py: 0.5, fontWeight: 600, fontStyle: 'italic', color: 'text.secondary' }}>
                                Subtotal:
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '13px', py: 0.5, fontWeight: 700, fontStyle: 'italic' }}>
                                {formatCurrency(groupReceiptTotal)}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* Summary Row */}
                    <TableRow sx={{ bgcolor: isDark ? 'rgba(76,175,80,0.08)' : '#e8f5e9' }}>
                      <TableCell colSpan={5} sx={{ fontSize: '13px', fontWeight: 700, py: 1.5 }}>
                        TOTALS ({Object.keys(invoiceGroups).length} invoices, {data.length} receipts)
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75 }}>
                        {formatCurrency(totalInvoiced)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75, color: '#4caf50' }}>
                        {formatCurrency(totalReceived)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={data.length} page={page}
            onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default InvoiceReceiptSummary;
